import { HubConnectionState } from "@microsoft/signalr";
import { loadWrapper } from "aic";
import { useAppSettings, useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { getHierarchyBaseUrl } from "contexts/hierarchy/getHierarchyBaseUrl";
import { SET_HIERARCHY_COMMENT_PANEL } from "contexts/hierarchy/interface";
import { HubGroupMessage } from "contexts/signalr/interface";
import { SignalRContext } from "contexts/SignalRProvider";
import { getTlfUrl } from "contexts/tlf/getTlfUrl";
import { SET_TLF_COMMENT_PANEL } from "contexts/tlf/interface";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { IRequestStatus } from "functions/api/interface";
import {
  NotificationViewDto,
  getUserNotifications,
} from "functions/api/notifications/getUserNotifications";
import { useContext, useEffect, useEffectEvent, useState, useTransition } from "react";
import { Badge, NavbarText } from "react-bootstrap";
import { Bell } from "react-bootstrap-icons";
import { useParams, useSearchParams } from "react-router-dom";
import { NotificationsPanel } from "./NotificationsPanel";

export interface NotificationsButtonProps {
  rightsDropMenuOpen: boolean;
  setRightsDropMenuOpen: (ret: boolean) => void;
}

/**
 * Top bar notifications button.
 *
 * Renders a notification bell with unread count and opens the notifications panel.
 */
export const NotificationsButton = (): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const tlfContext = useTlfContext();

  const signalR = useContext(SignalRContext);
  const connection = signalR?.connection;
  const { navigate } = useUnsavedChanges("topbar-notifications-button");
  const [searchParams] = useSearchParams();
  const urlParams = useParams();
  const [loadRequest, setLoadRequest] = useState<IRequestStatus<string>>({
    requesting: false,
    error: false,
  });
  const [notificationsList, setNotificationsList] = useState<NotificationViewDto[]>([]);
  const [showNotifications, setShowNotifications] = useState<boolean>(false);
  const newNotificationCount = notificationsList.filter((rn) => !rn.notificationViewed).length;
  const loadNotifications = useEffectEvent(async () => {
    const response = await loadWrapper(
      getUserNotifications,
      "load",
      loadRequest,
      setLoadRequest,
      appSettings.backEndUrl,
    );

    if (response?.success) {
      startTransition(() => setNotificationsList(response.data));
    }
  });

  useEffect(() => {
    if (!loadRequest.requesting && !loadRequest.requestedId && !loadRequest.error)
      loadNotifications();
  }, [loadRequest]);

  const [sendGroupRequest, setSendGroupRequest] = useState<boolean>(false);
  useEffect(() => {
    if (
      connection &&
      connection.state === HubConnectionState.Connected &&
      !sendGroupRequest &&
      appSettings.userId
    ) {
      signalR.connectToGroup(`user-${appSettings.userId}`);
      startTransition(() => {
        setSendGroupRequest(true);
      });
    }
  }, [appSettings.userId, connection, connection?.state, sendGroupRequest, signalR]);

  /**
   * Process comments update messages from back end
   */
  useEffect(() => {
    /**
     * Process a SignalR group message containing comments updates.
     *
     * @param message - JSON string payload describing the update
     */
    const processCommentsMessage = (message: string) => {
      const update = JSON.parse(message) as HubGroupMessage;
      if (update.messageType === "commentsUpdate" && update.comments) {
        setNotificationsList(update.comments);
      }
    };
    signalR?.connection?.on("SendMessageToGroup", processCommentsMessage);
    // return () => {
    //   signalR?.connection?.off("SendMessageToGroup", processCommentsMessage);
    // };
  }, [signalR]);

  const setCommentPanelStatusInContext = useEffectEvent(
    (
      highlightCommentId: string,
      showComments: boolean,
      notificationContext: "TlfItem" | "Hierarchy",
    ) => {
      let redirectUrl = "";

      /* istanbul ignore else */
      if (notificationContext === "Hierarchy" && hierarchyContext?.hierarchy) {
        hierarchyContext.dispatch({
          operation: SET_HIERARCHY_COMMENT_PANEL,
          commentPanelStatus: {
            ...hierarchyContext.commentPanelStatus,
            highlightCommentId,
            showComments,
          },
        });
        redirectUrl = getHierarchyBaseUrl(hierarchyContext.hierarchy);
      } else if (
        notificationContext === "TlfItem" &&
        tlfContext.tlfData &&
        hierarchyContext?.hierarchy
      ) {
        tlfContext.dispatch({
          operation: SET_TLF_COMMENT_PANEL,
          commentPanelStatus: {
            ...tlfContext.commentPanelStatus,
            highlightCommentId,
            showComments,
          },
          calledFrom: "NotificationsButton:setCommentPanelStatusInContext",
        });
        redirectUrl = getTlfUrl(hierarchyContext.hierarchy, tlfContext.tlfData);
      }

      /* istanbul ignore else */
      if (redirectUrl) {
        navigate(redirectUrl, { replace: true });
      }
    },
  );

  // Handle Comments Panel status on redirection from Notification
  useEffect(() => {
    if (
      (searchParams.has("showHierarchyComments") || searchParams.has("showTlfItemComments")) &&
      hierarchyContext?.hierarchy
    ) {
      const commentIdFromNotification = searchParams.get("commentId");
      /* istanbul ignore else */
      if (searchParams.get("showHierarchyComments") === "true") {
        // A valid notification comment is handled above; the fallback only opens the panel when it is closed.
        /* istanbul ignore else */
        if (
          commentIdFromNotification &&
          hierarchyContext.hierarchy.comments.some((c) => c.id === commentIdFromNotification) &&
          hierarchyContext.commentPanelStatus.highlightCommentId !== commentIdFromNotification
        ) {
          setCommentPanelStatusInContext(commentIdFromNotification, true, "Hierarchy");
        } else if (!hierarchyContext.commentPanelStatus.showComments) {
          setCommentPanelStatusInContext("", true, "Hierarchy");
        }
      } else if (searchParams.get("showTlfItemComments") === "true" && tlfContext.tlfData) {
        /* istanbul ignore else */
        if (
          commentIdFromNotification &&
          tlfContext.tlfData.comments.some((c) => c.id === commentIdFromNotification) &&
          tlfContext.commentPanelStatus.highlightCommentId !== commentIdFromNotification
        ) {
          setCommentPanelStatusInContext(commentIdFromNotification, true, "TlfItem");
        } else if (!tlfContext.commentPanelStatus.showComments) {
          setCommentPanelStatusInContext("", true, "TlfItem");
        }
      }
    }
  }, [hierarchyContext, tlfContext, searchParams, urlParams]);

  return (
    <>
      {showNotifications && (
        <NotificationsPanel
          notificationsList={notificationsList}
          setNotificationsList={setNotificationsList}
          showNotifications={showNotifications}
          setShowNotifications={setShowNotifications}
          loadNotificationsRequest={loadRequest}
        />
      )}
      <NavbarText
        id="topbar-notifications-button"
        style={{ cursor: "pointer" }}
        title="View Notifications"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowNotifications(!showNotifications);
        }}
      >
        <div
          style={{
            minWidth: "32px",
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
          }}
          aria-label="Show notifications"
        >
          {newNotificationCount > 0 ? (
            <Badge
              pill
              bg="primary"
              aria-label={`${newNotificationCount} new notification${newNotificationCount !== 1 ? "s" : ""}`}
            >
              {newNotificationCount < 10 ? newNotificationCount : "9+"}
            </Badge>
          ) : (
            <Bell size={20} />
          )}
        </div>
      </NavbarText>
    </>
  );
};

NotificationsButton.displayName = "NotificationsButton";
